import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { api, apiPdfUrl } from '@/lib/api';
import { useI18n, formatCurrency } from '@/lib/i18n';
import { useBranding } from '@/lib/branding';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Plus, Send, Printer, X, CreditCard, Receipt as ReceiptIcon, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

/**
 * Trigger silent print of a PDF using a hidden iframe.
 * Works with any OS-installed printer (thermal POS or A4) and lets the
 * browser print dialog auto-select the default printer if the OS allows.
 */
function printPdfInIframe(url) {
  const existing = document.getElementById('__pos_print_iframe');
  if (existing) existing.remove();
  const iframe = document.createElement('iframe');
  iframe.id = '__pos_print_iframe';
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.src = url;
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch (_) {
        window.open(url, '_blank');
      }
    }, 300);
    setTimeout(() => iframe.remove(), 30_000);
  };
  document.body.appendChild(iframe);
}

const STATUS_RING = {
  libre: 'ring-transparent',
  ocupada: 'ring-emerald-500/60',
  reservada: 'ring-amber-500/60',
  fuera_servicio: 'ring-rose-500/60',
};

function TableTile({ table, onClick, t }) {
  return (
    <Card
      onClick={onClick}
      className={cn(
        'cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5 ring-2',
        STATUS_RING[table.estado] || 'ring-transparent'
      )}
      data-testid={`pos-table-${table.numero}`}
    >
      <CardContent className="p-4 text-center">
        <div className="text-3xl font-bold mb-1">{table.numero}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wide">
          {t(`status_${table.estado}`)}
        </div>
        <div className="text-xs mt-1">{table.capacidad} pax</div>
      </CardContent>
    </Card>
  );
}

export default function Pos() {
  const { t, lang } = useI18n();
  const { branding } = useBranding();
  const autoPrint = !!branding?.auto_print_kitchen;
  const printerSize = branding?.kitchen_printer_size || '80mm';
  const [tables, setTables] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [createTableOpen, setCreateTableOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closePayload, setClosePayload] = useState({
    tipo: 'ticket',
    pago_metodo: 'tarjeta',
    cliente_nombre: '',
    cliente_nif: '',
    cliente_direccion: '',
  });

  // -------- Item edit dialog (quantity + notes) --------
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [editItem, setEditItem] = useState(null); // {id, cantidad, notas, nombre}

  const loadTables = useCallback(async () => {
    const r = await api.get('/tables');
    setTables(r.data);
  }, []);

  const loadMenu = useCallback(async () => {
    const [c, m] = await Promise.all([api.get('/menu-categories'), api.get('/menu-items', { params: { activo: true } })]);
    setCategories(c.data);
    setMenuItems(m.data);
    setActiveCategory((prev) => prev || (c.data[0]?.id ?? null));
  }, []);

  useEffect(() => {
    loadTables();
    loadMenu();
  }, [loadTables, loadMenu]);

  const filteredItems = useMemo(
    () => menuItems.filter((m) => !activeCategory || m.categoria_id === activeCategory),
    [menuItems, activeCategory]
  );

  const openTable = async (table) => {
    const r = await api.get(`/orders`, { params: { table_id: table.id, activo: true } });
    if (r.data && r.data.length > 0) {
      setActiveOrder(r.data[0]);
    } else {
      const created = await api.post(`/orders`, {
        table_id: table.id,
        table_numero: table.numero,
        tipo: 'mesa',
        comensales: 1,
      });
      setActiveOrder(created.data);
    }
  };

  const newTakeaway = async () => {
    const r = await api.post(`/orders`, {
      table_id: null,
      table_numero: null,
      tipo: 'mesa',
      comensales: 1,
    });
    setActiveOrder(r.data);
    loadTables();
  };

  const addItem = async (mi) => {
    if (!activeOrder) return;
    const r = await api.post(`/orders/${activeOrder.id}/items`, {
      menu_item_id: mi.id,
      cantidad: 1,
    });
    const updated = await api.get(`/orders/${activeOrder.id}`);
    setActiveOrder(updated.data);
  };

  // Click long / right-click on product card: add then immediately open edit dialog
  const addItemAndEdit = async (mi) => {
    if (!activeOrder) return;
    const r = await api.post(`/orders/${activeOrder.id}/items`, {
      menu_item_id: mi.id,
      cantidad: 1,
    });
    const updated = await api.get(`/orders/${activeOrder.id}`);
    setActiveOrder(updated.data);
    const newId = r?.data?.item?.id;
    if (newId) {
      const newItem = (updated.data.items || []).find((it) => it.id === newId);
      if (newItem) {
        setEditItem({
          id: newItem.id,
          cantidad: newItem.cantidad || 1,
          notas: newItem.notas || '',
          nombre: newItem.nombre,
        });
        setEditItemOpen(true);
      }
    }
  };

  const removeItem = async (iid) => {
    if (!activeOrder) return;
    await api.delete(`/orders/${activeOrder.id}/items/${iid}`);
    const updated = await api.get(`/orders/${activeOrder.id}`);
    setActiveOrder(updated.data);
  };

  const openEditItem = (it) => {
    setEditItem({
      id: it.id,
      cantidad: it.cantidad || 1,
      notas: it.notas || '',
      nombre: it.nombre,
    });
    setEditItemOpen(true);
  };

  const saveEditItem = async () => {
    if (!activeOrder || !editItem) return;
    const qty = Math.max(1, parseInt(editItem.cantidad, 10) || 1);
    await api.put(`/orders/${activeOrder.id}/items/${editItem.id}`, {
      cantidad: qty,
      notas: editItem.notas || null,
    });
    const updated = await api.get(`/orders/${activeOrder.id}`);
    setActiveOrder(updated.data);
    setEditItemOpen(false);
    setEditItem(null);
  };

  const sendKitchen = async () => {
    if (!activeOrder) return;
    const r = await api.post(`/orders/${activeOrder.id}/send-to-kitchen`);
    setActiveOrder(r.data);
    toast.success(lang === 'es' ? 'Enviado a cocina' : 'Enviado à cozinha');
    if (autoPrint) {
      const url = apiPdfUrl(`/orders/${activeOrder.id}/kitchen-pdf?size=${printerSize}`);
      printPdfInIframe(url);
    }
  };

  const printKitchen80 = () => {
    if (!activeOrder) return;
    printPdfInIframe(apiPdfUrl(`/orders/${activeOrder.id}/kitchen-pdf?size=80mm`));
  };

  const printKitchenA4 = () => {
    if (!activeOrder) return;
    window.open(apiPdfUrl(`/orders/${activeOrder.id}/kitchen-pdf?size=a4`), '_blank');
  };

  const closeOrder = async () => {
    if (!activeOrder) return;
    try {
      const r = await api.post(`/orders/${activeOrder.id}/close`, closePayload);
      toast.success(lang === 'es' ? 'Pedido cerrado' : 'Pedido fechado');
      window.open(apiPdfUrl(`/invoices/${r.data.id}/pdf`), '_blank');
      setActiveOrder(null);
      setCloseOpen(false);
      loadTables();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const createTable = async (form) => {
    await api.post(`/tables`, form);
    setCreateTableOpen(false);
    loadTables();
  };

  // ============================================================
  // ACTIVE ORDER VIEW
  // ============================================================
  if (activeOrder) {
    return (
      <div className="h-screen flex flex-col">
        <div className="border-b bg-[hsl(var(--card))] flex items-center gap-3 px-3 py-2">
          <Button variant="ghost" size="sm" onClick={() => { setActiveOrder(null); loadTables(); }} data-testid="pos-back-button">
            <ArrowLeft className="h-4 w-4 mr-1" />
          </Button>
          <div className="flex-1">
            <div className="font-heading font-semibold">
              {activeOrder.tipo === 'mesa' && activeOrder.table_numero
                ? `${t('tables')} • #${activeOrder.table_numero}`
                : t('takeaway')}
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">{activeOrder.estado}</div>
          </div>
          <Badge className="font-mono">{formatCurrency(activeOrder.total, lang)}</Badge>
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr_320px] overflow-hidden">
          {/* Categories sidebar */}
          <div className="border-r bg-[hsl(var(--card))] p-2 overflow-y-auto">
            <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2 px-2">
              {t('category')}
            </div>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCategory(c.id)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-lg text-sm mb-1 transition-colors',
                  activeCategory === c.id
                    ? 'bg-[hsl(var(--primary))] text-white'
                    : 'hover:bg-[hsl(var(--muted))]'
                )}
                data-testid={`pos-category-${c.id}`}
              >
                {c.nombre}
              </button>
            ))}
            {categories.length === 0 && (
              <p className="text-xs text-[hsl(var(--muted-foreground))] px-2 py-4">
                {lang === 'es' ? 'Crea categorías en Menú.' : 'Crie categorias em Cardápio.'}
              </p>
            )}
          </div>

          {/* Items */}
          <div className="overflow-y-auto p-3 grid grid-cols-2 sm:grid-cols-3 gap-2 content-start">
            {filteredItems.map((mi) => (
              <button
                key={mi.id}
                onClick={() => addItem(mi)}
                onContextMenu={(e) => { e.preventDefault(); addItemAndEdit(mi); }}
                title={lang === 'es' ? 'Click: añadir | Click derecho: añadir + observación' : 'Clique: adicionar | Clique direito: adicionar + observação'}
                className="rounded-xl border p-3 text-left bg-[hsl(var(--card))] hover:shadow-md hover:-translate-y-0.5 transition-transform active:scale-[0.98]"
                data-testid={`pos-menu-item-${mi.id}`}
              >
                <div className="font-medium text-sm leading-tight mb-1 line-clamp-2">{mi.nombre}</div>
                <div className="flex items-center justify-between mt-2">
                  <Badge variant="outline" className="text-[10px]">{mi.iva_pct}% IVA</Badge>
                  <div className="font-mono font-semibold text-sm">{formatCurrency(mi.pvp, lang)}</div>
                </div>
              </button>
            ))}
            {filteredItems.length === 0 && (
              <p className="col-span-full text-center text-sm text-[hsl(var(--muted-foreground))] py-12">
                {lang === 'es' ? 'Sin platos en esta categoría' : 'Sem pratos nesta categoria'}
              </p>
            )}
          </div>

          {/* Ticket */}
          <div className="border-l bg-[hsl(var(--card))] flex flex-col">
            <div className="p-3 border-b">
              <div className="font-heading font-semibold">{t('new_order')}</div>
            </div>
            <ScrollArea className="flex-1 p-2">
              {(activeOrder.items || []).length === 0 ? (
                <p className="text-center text-sm text-[hsl(var(--muted-foreground))] py-12">
                  {lang === 'es' ? 'Añade platos al pedido' : 'Adicione pratos ao pedido'}
                </p>
              ) : (
                activeOrder.items.map((it) => (
                  <div key={it.id} className="flex items-start gap-2 px-2 py-2 border-b last:border-0" data-testid={`pos-ticket-item-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        {it.nombre} <span className="text-xs text-[hsl(var(--muted-foreground))]">x{it.cantidad}</span>
                      </div>
                      {it.notas && (
                        <div className="text-[11px] italic text-[hsl(var(--muted-foreground))] mt-0.5 break-words">
                          {it.notas}
                        </div>
                      )}
                      {it.estado !== 'nuevo' && (
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase mt-0.5">
                          {t(`kds_status_${it.estado === 'preparando' ? 'preparing' : it.estado === 'listo' ? 'ready' : 'served'}`)}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm">{formatCurrency(it.pvp * it.cantidad, lang)}</div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditItem(it)}
                      className="h-7 w-7"
                      title={lang === 'es' ? 'Cantidad / observación' : 'Quantidade / observação'}
                      data-testid={`pos-ticket-edit-${it.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => removeItem(it.id)} className="h-7 w-7" data-testid={`pos-ticket-remove-${it.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </ScrollArea>
            <div className="p-3 border-t space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-[hsl(var(--muted-foreground))]">{t('base')}</span>
                <span className="font-mono">{formatCurrency(activeOrder.subtotal, lang)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-[hsl(var(--muted-foreground))]">{t('iva')}</span>
                <span className="font-mono">{formatCurrency(activeOrder.iva_total, lang)}</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>{t('total')}</span>
                <span className="font-mono">{formatCurrency(activeOrder.total, lang)}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button onClick={sendKitchen} size="lg" className="col-span-2" data-testid="pos-send-to-kitchen-button" disabled={!(activeOrder.items || []).length}>
                  <Send className="h-4 w-4 mr-2" />
                  {t('send_to_kitchen')}
                </Button>
                <Button onClick={printKitchen80} variant="outline" data-testid="pos-print-kitchen-80mm-button" disabled={!(activeOrder.items || []).length} title="Imprimir 80mm térmica">
                  <Printer className="h-4 w-4 mr-1" /> 80mm
                </Button>
                <Button onClick={printKitchenA4} variant="outline" data-testid="pos-print-kitchen-pdf-button" disabled={!(activeOrder.items || []).length} title="PDF A4">
                  <Printer className="h-4 w-4 mr-1" /> A4
                </Button>
                <Button onClick={() => setCloseOpen(true)} variant="secondary" className="col-span-2" data-testid="pos-close-order-button" disabled={!(activeOrder.items || []).length}>
                  <CreditCard className="h-4 w-4 mr-2" />
                  {t('close_order')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* ========= EDIT ITEM DIALOG (quantity + notes) ========= */}
        <Dialog open={editItemOpen} onOpenChange={(o) => { if (!o) { setEditItemOpen(false); setEditItem(null); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editItem?.nombre}</DialogTitle>
            </DialogHeader>
            {editItem && (
              <div className="space-y-3">
                <div>
                  <Label>{lang === 'es' ? 'Cantidad' : 'Quantidade'}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setEditItem({ ...editItem, cantidad: Math.max(1, (parseInt(editItem.cantidad, 10) || 1) - 1) })}
                    >
                      −
                    </Button>
                    <Input
                      type="number"
                      min={1}
                      value={editItem.cantidad}
                      onChange={(e) => setEditItem({ ...editItem, cantidad: e.target.value })}
                      className="text-center font-mono w-20"
                      data-testid="pos-edit-qty-input"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setEditItem({ ...editItem, cantidad: (parseInt(editItem.cantidad, 10) || 1) + 1 })}
                    >
                      +
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>{lang === 'es' ? 'Observación (cocina)' : 'Observação (cozinha)'}</Label>
                  <textarea
                    rows={4}
                    value={editItem.notas}
                    onChange={(e) => setEditItem({ ...editItem, notas: e.target.value })}
                    placeholder={lang === 'es'
                      ? 'Ej: sin cebolla, punto de carne medio, alergia a frutos secos...'
                      : 'Ex: sem cebola, ponto da carne ao ponto, alergia a oleaginosas...'}
                    className="mt-1 w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    data-testid="pos-edit-notes-input"
                  />
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">
                    {lang === 'es'
                      ? 'Aparecerá en la comanda impresa para la cocina.'
                      : 'Aparecerá na comanda impressa para a cozinha.'}
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEditItemOpen(false); setEditItem(null); }}>
                {t('cancel')}
              </Button>
              <Button onClick={saveEditItem} data-testid="pos-edit-save-button">
                {t('save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ========= CLOSE ORDER DIALOG ========= */}
        <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('close_order')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t('invoice_type')}</Label>
                <Select value={closePayload.tipo} onValueChange={(v) => setClosePayload({ ...closePayload, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ticket">{t('ticket')}</SelectItem>
                    <SelectItem value="factura_simplificada">{t('simplified_invoice')}</SelectItem>
                    <SelectItem value="factura">{t('full_invoice')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('payment_method')}</Label>
                <Select value={closePayload.pago_metodo} onValueChange={(v) => setClosePayload({ ...closePayload, pago_metodo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">{t('cash')}</SelectItem>
                    <SelectItem value="tarjeta">{t('card')}</SelectItem>
                    <SelectItem value="transferencia">{t('transfer')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {closePayload.tipo === 'factura' && (
                <>
                  <div>
                    <Label>{t('client_name')}</Label>
                    <Input value={closePayload.cliente_nombre} onChange={(e) => setClosePayload({ ...closePayload, cliente_nombre: e.target.value })} />
                  </div>
                  <div>
                    <Label>NIF</Label>
                    <Input value={closePayload.cliente_nif} onChange={(e) => setClosePayload({ ...closePayload, cliente_nif: e.target.value })} />
                  </div>
                  <div>
                    <Label>{t('address')}</Label>
                    <Input value={closePayload.cliente_direccion} onChange={(e) => setClosePayload({ ...closePayload, cliente_direccion: e.target.value })} />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCloseOpen(false)}>{t('cancel')}</Button>
              <Button onClick={closeOrder} data-testid="pos-close-confirm-button">
                <ReceiptIcon className="h-4 w-4 mr-2" />
                {t('issue_invoice')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============================================================
  // TABLES VIEW
  // ============================================================
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold">{t('tables')}</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('select_table_or_takeaway')}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateTableOpen(true)} variant="outline" data-testid="pos-create-table-button">
            <Plus className="h-4 w-4 mr-1" />
            {t('add_table')}
          </Button>
          <Button onClick={newTakeaway} data-testid="pos-new-takeaway-button">
            <Plus className="h-4 w-4 mr-1" />
            {t('takeaway')}
          </Button>
        </div>
      </div>
      {tables.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
              {lang === 'es'
                ? 'No hay mesas creadas. Crea la primera para empezar.'
                : 'Sem mesas. Crie a primeira para começar.'}
            </p>
            <Button onClick={() => setCreateTableOpen(true)} data-testid="pos-empty-create-button">
              <Plus className="h-4 w-4 mr-1" />
              {t('add_table')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {tables.map((table) => (
            <TableTile key={table.id} table={table} onClick={() => openTable(table)} t={t} />
          ))}
        </div>
      )}
      <CreateTableDialog open={createTableOpen} onOpenChange={setCreateTableOpen} onCreate={createTable} t={t} />
    </div>
  );
}

function CreateTableDialog({ open, onOpenChange, onCreate, t }) {
  const [form, setForm] = useState({ numero: '', capacidad: 4, zona: 'Salón' });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t('new_table')}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label>{t('number')}</Label><Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} /></div>
          <div><Label>{t('capacity')}</Label><Input type="number" value={form.capacidad} onChange={(e) => setForm({ ...form, capacidad: parseInt(e.target.value) || 4 })} /></div>
          <div><Label>{t('zone')}</Label>
            <Select value={form.zona} onValueChange={(v) => setForm({ ...form, zona: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Salón">{t('zone_main')}</SelectItem>
                <SelectItem value="Terraza">{t('zone_terrace')}</SelectItem>
                <SelectItem value="Barra">{t('zone_bar')}</SelectItem>
                <SelectItem value="Privado">{t('zone_private')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('cancel')}</Button>
          <Button onClick={() => onCreate(form)} disabled={!form.numero} data-testid="create-table-confirm-button">{t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
